<template>
  <cfs-date-picker
    :value="value"
    :is-range="range"
    title-position="left"
    :drag-attribute="vCalendarAttributes"
    :select-attribute="vCalendarAttributes"
    :model-config="modelConfig"
    v-bind="$attrs"
    :mode="mode"
    :timezone="currentOrganizationTimeZone"
    @input="onChange"
  >
    <template v-if="useTemplate" #default="{ togglePopover, inputValue }">
      <div v-if="!readonly" class="cfs-field d-flex flex-column" :class="{ 'static-bg': staticBg }">
        <p v-if="label" class="input-title px-1 pb-1" :class="{ disabled }">{{ label }}</p>

        <v-text-field
          ref="outer"
          :tabindex="tabindex"
          class="cfs-date-input"
          :placeholder="inputPlaceholder"
          readonly
          outlined
          :disabled="disabled"
          :append-icon="appendIcon"
          dense
          :value="parseDatePickerValue(inputValue)"
          :hide-details="hideDetails"
          :rules="rules"
          @click="!disabled && togglePopover()"
        >
          <template #append>
            <slot v-if="$hasSlot('append')" name="append" />

            <div v-else class="flex gap-2" :class="{ 'pointer-events-none': disabled }">
              <v-menu
                v-if="showTimezoneList"
                :close-on-content-click="false"
                content-class="shadow-none date-menu"
                absolute
                :position-x="outer?.$el?.getBoundingClientRect()?.left"
                :position-y="outer?.$el?.getBoundingClientRect()?.top - 50"
              >
                <template #default>
                  <div class="date-menu-caret"></div>
                  <CfsAutocomplete
                    v-if="showTimezoneList"
                    color="#cccccc"
                    class="bg-white"
                    :style="{
                      width: outer?.$el.clientWidth + 'px',
                    }"
                    hide-details
                    :value="currentOrganizationTimeZone"
                    :items="timezonesList"
                    :disabled="disabled"
                    item-value="code"
                    item-text="description"
                    :return-object="false"
                    @change="$emit('update:preferred-timezone', $event)"
                  />
                </template>
                <template #activator="{ on, attrs }">
                  <v-icon title="Timezone" v-bind="attrs" v-on="on">mdi-map-clock-outline</v-icon>
                </template>
              </v-menu>
              <v-icon
                v-if="!loading"
                :tabindex="tabindex"
                @click="!disabled && outerClickHandler(togglePopover)"
              >
                {{ appendIcon }}
              </v-icon>
              <v-progress-circular
                v-if="loading"
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>
          </template>
        </v-text-field>
        <small v-if="hint.length && disabled" class="pl-2 text-xs text-blue-havelock">
          * {{ hint }}
        </small>
      </div>
      <CfsReadonlyField v-else v-bind="$props" :value="parseDatePickerValue(inputValue)" />
    </template>
  </cfs-date-picker>
</template>

<script lang="ts">
import { computed, defineAsyncComponent, defineComponent, PropType, ref } from 'vue';
import { isObject } from '@/helpers/objects';
import { DateFormat } from '@/extensions/filters/date';
import { useOrganizationStore } from '@/store/modules/useOrganizationStore';
import { useDateFormatContext } from '@/composables/date/useDateFormatContext';
import useFetchTimezonesQuery from '@/composables/references/useFetchTimezonesQuery';
import { VTextField } from 'vuetify/lib/components';

interface VCalendarDateRange {
  start: string;
  end: string;
}

type VCalendarValue = VCalendarDateRange | string | Date | null;
type VCalendarMode = 'date' | 'time' | 'dateTime' | 'month';

const DATE_PICKER_HIGHLIGHT_COLOR = '#BE9150';
const DATE_PICKER_HIGHLIGHT_OPTION = {
  style: {
    backgroundColor: DATE_PICKER_HIGHLIGHT_COLOR,
  },
  fillMode: 'solid',
};
const DATE_PICKER_HIGHLIGHT_BASE_OPTION = {
  style: {
    backgroundColor: '#BE915024',
  },
  contentStyle: {
    color: '#fff',
  },
  fillMode: 'light',
};
const DATE_PICKER_OPTIONS = {
  end: DATE_PICKER_HIGHLIGHT_OPTION,
  start: DATE_PICKER_HIGHLIGHT_OPTION,
  base: DATE_PICKER_HIGHLIGHT_BASE_OPTION,
};
const DATE_PICKER_MODE_MASK: Record<VCalendarMode, DateFormat> = {
  time: DateFormat.Time,
  date: DateFormat.Default,
  dateTime: DateFormat.FullDate,
} as const;

export default defineComponent({
  name: 'CfsDateFieldComponent',
  components: {
    CfsReadonlyField: defineAsyncComponent(() => import('./special/CfsReadonlyFieldComponent.vue')),
  },
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    data: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    tabindex: {
      type: [Number, String],
    },
    value: {
      type: [String, Object, Date] as PropType<VCalendarValue>,
      default: null,
    },
    /**when set to false fills input with application background solid color instead of inherited one.
     * I.e. removes transparency from input*/
    staticBg: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    range: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String as PropType<VCalendarMode>,
      default: 'date',
    },
    timezone: {
      type: String,
      default: 'utc',
    },
    setToUtc: {
      type: Boolean,
      default: true,
    },
    useTimeZone: {
      type: Boolean,
      default: true,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    hideDetails: {
      type: Boolean,
      default: true,
    },
    useTemplate: {
      type: Boolean,
      default: true,
    },
    preferredTimezone: {
      type: String,
      default: null,
    },
    floorRangeDates: {
      type: Boolean,
      default: true,
    },
    showTimezoneList: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change', 'update:preferred-timezone'],
  setup(props, { emit }) {
    const organizationStore = useOrganizationStore();
    const { timezone: contextTimezone } = useDateFormatContext();
    const { data: timezonesList } = useFetchTimezonesQuery(computed(() => props.showTimezoneList));

    const currentOrganizationTimeZone = computed(() => {
      if (props.preferredTimezone) {
        return props.preferredTimezone;
      }
      if (contextTimezone?.value) {
        return contextTimezone.value;
      }
      if (!organizationStore.currentOrganizationTimeZone) {
        return props.timezone;
      }
      return organizationStore.currentOrganizationTimeZone;
    });

    const inputPlaceholder = computed(() => {
      return props.placeholder || 'Select Date';
    });

    const vCalendarAttributes = computed(() => ({
      highlight: DATE_PICKER_OPTIONS,
    }));

    const modelConfig = computed(() => {
      return {
        mask: DATE_PICKER_MODE_MASK[props.mode] || DateFormat.Default,
        ...(props.range && props.floorRangeDates
          ? {
              start: {
                timeAdjust: '00:00:00',
              },
              end: {
                timeAdjust: '23:59:59',
              },
            }
          : {}),
      };
    });

    const appendIcon = computed(() => {
      return props.clearable && props.value ? 'mdi-close' : 'mdi-calendar-blank-outline';
    });

    const onChange = (date: VCalendarValue) => {
      if (date instanceof Date) {
        if (props.mode === 'date' && props.setToUtc) {
          const newDate = new Date();
          date.setUTCHours(newDate.getUTCHours());
          date.setUTCMinutes(newDate.getUTCMinutes());
        }

        return emit('change', date.toISOString());
      }
      if (isObject(date)) {
        return emit('change', date);
      }
      emit('change', date);
    };

    const outer = ref<InstanceType<typeof VTextField> | null>(null);

    const outerClickHandler = (togglePopover: () => void) => {
      if (appendIcon.value === 'mdi-close') {
        emit('change', null);
      } else {
        togglePopover();
      }
    };

    const parseDatePickerValue = (date: VCalendarValue) => {
      if (!date) {
        return '';
      }

      if (isObject(date) && !(date instanceof Date)) {
        if (!(date?.start && date?.end)) {
          return '';
        }
        const { start, end } = date;
        return `${start} - ${end}`;
      }

      return date;
    };

    return {
      outer,
      appendIcon,
      modelConfig,
      timezonesList,
      inputPlaceholder,
      vCalendarAttributes,
      currentOrganizationTimeZone,
      onChange,
      outerClickHandler,
      parseDatePickerValue,
    };
  },
});
</script>

<style lang="scss" scoped>
.date-menu {
  overflow: initial;
  contain: none;

  &-caret {
    background: white;
    width: 14px;
    aspect-ratio: 1/1;

    position: absolute;

    border: 1.3px solid #cccccc;
    border-left: none;
    border-top: none;

    bottom: -13.02px;
    right: 17px;
    z-index: 9;

    transform: translateY(-50%) rotate(45deg);
  }
}

::v-deep .vc-title {
  color: #737373 !important;
  font-size: 1rem;
  line-height: 1.5rem;

  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    background: url('@/assets/img/icons/arrow-down.svg') center / contain no-repeat;
  }
}

::v-deep {
  .vc-popover-content-wrapper {
    .vc-time-picker {
      .vc-date {
        .vc-month,
        .vc-day {
          color: var(--v-primary-base) !important;
        }
      }

      .vc-time {
        .vc-am-pm {
          button {
            &:focus {
              border-color: var(--v-primary-base) !important;
            }

            &.active {
              background-color: var(--v-primary-base) !important;
            }
          }
        }
      }
    }

    .vc-container {
      border-color: #cccccc;

      .vc-popover-content-wrapper {
        transform: none !important;
        width: 100%;

        .vc-popover-content {
          background-color: var(--v-alabaster-base) !important;
          border-color: #cccccc;

          height: 100%;
          margin-top: 0;

          color: #444444;

          .vc-nav-title {
            color: #737373;

            &:hover {
              background-color: transparent;
              color: inherit;
              box-shadow: none;
            }
          }

          .vc-nav-items {
            justify-items: center;

            .vc-nav-item {
              &.is-active {
                color: white !important;
                border-radius: 50px !important;
                background: var(--v-primary-base) !important;
                width: 100%;
              }

              &:hover {
                background-color: transparent;
                color: inherit;
                box-shadow: none;
              }

              &.is-current {
                color: inherit;
                border: none;
              }
            }
          }
        }
      }
    }
  }
}
</style>
